import { useCallback, useEffect, useRef, useState } from 'react';
import { Surface } from '@cloudflare/kumo/components/surface';
import { Button } from '@cloudflare/kumo/components/button';
import { Badge } from '@cloudflare/kumo/components/badge';
import { Banner } from '@cloudflare/kumo/components/banner';
import { Field } from '@cloudflare/kumo/components/field';
import { Input, Textarea } from '@cloudflare/kumo/components/input';
import { Radio } from '@cloudflare/kumo/components/radio';
import { Table } from '@cloudflare/kumo/components/table';
import { Megaphone, PaperPlaneTilt, TestTube, TrashSimple, Plus } from '@phosphor-icons/react';
import { api } from '@/lib/api';

// 管理画面からのメール一括送信 (メルマガ / 出展者向けお知らせ)。
// 2 系統を明確に分ける:
//   newsletter            … 全アカウントへの告知。配信停止 (opt-out) あり。
//   exhibitor-announcement… 今年の出展者への業務連絡。配信停止なし (取引関係に基づく連絡)。
// enqueue したら実送信は Cron drain が毎分少量ずつ行う (50 通打ち切りを構造的に回避)。

type Kind = 'newsletter' | 'exhibitor-announcement';

interface CampaignState {
  id: string;
  status: string;
  total: number;
}

interface Progress {
  campaign: { id: string; status: string; totalCount: number; kind: string };
  counts: Record<string, number>;
  failed: Array<{ recipientEmail: string; error: string | null; attempts: number }>;
}

interface Suppression {
  email: string;
  reason: string;
  createdAt: string;
}

const ANNOUNCEMENT_STATUSES: Array<{ value: string; label: string; description: string }> = [
  { value: 'paid', label: '出展確定者 (支払い完了)', description: '入金が済み出展が確定した人。通常はこれ。' },
  { value: 'waitingForPay', label: '請求済み・未入金', description: 'status = waitingForPay の人。' },
  { value: 'checkedByAdmin', label: '内定済み (未請求)', description: 'status = checkedByAdmin の人。' },
];

type BodyFormat = 'wrapped' | 'raw';

export default function BroadcastPage() {
  const [kind, setKind] = useState<Kind>('newsletter');
  const [audienceStatus, setAudienceStatus] = useState('paid');
  const [subject, setSubject] = useState('');
  const [bodyFormat, setBodyFormat] = useState<BodyFormat>('wrapped');
  const [html, setHtml] = useState('');
  const [text, setText] = useState('');

  const [previewCount, setPreviewCount] = useState<number | null>(null);
  const [campaign, setCampaign] = useState<CampaignState | null>(null);
  const [progress, setProgress] = useState<Progress | null>(null);
  const [testEmail, setTestEmail] = useState('');

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  // 配信対象の事前件数を取得 (compose 中、kind / audienceStatus 変更時)。
  const fetchPreview = useCallback(async () => {
    setPreviewCount(null);
    try {
      const params = new URLSearchParams({ kind });
      if (kind === 'exhibitor-announcement') params.set('audienceStatus', audienceStatus);
      const res = await api.get(`/api/admin/email-campaigns/preview-recipients?${params.toString()}`);
      const data = (res.data?.data ?? res.data) as { count: number };
      setPreviewCount(data.count);
    } catch (e) {
      setError(extractError(e));
    }
  }, [kind, audienceStatus]);

  useEffect(() => {
    if (!campaign) fetchPreview();
  }, [fetchPreview, campaign]);

  // 送信中はキャンペーン進捗をポーリングする。
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const loadProgress = useCallback(async (id: string) => {
    try {
      const res = await api.get(`/api/admin/email-campaigns/${id}`);
      const data = (res.data?.data ?? res.data) as Progress;
      setProgress(data);
      setCampaign((prev) => (prev ? { ...prev, status: data.campaign.status } : prev));
      return data.campaign.status;
    } catch {
      return null;
    }
  }, []);

  useEffect(() => {
    if (!campaign || campaign.status !== 'sending') {
      if (pollRef.current) clearInterval(pollRef.current);
      return;
    }
    loadProgress(campaign.id);
    pollRef.current = setInterval(() => loadProgress(campaign.id), 3000);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [campaign?.id, campaign?.status, loadProgress]);

  const createDraft = async () => {
    setError(null);
    setInfo(null);
    if (!subject.trim()) return setError('件名を入力してください');
    if (!html.trim()) return setError('HTML 本文を入力してください');
    setBusy(true);
    try {
      const res = await api.post('/api/admin/email-campaigns', {
        kind,
        subject,
        html,
        text,
        bodyFormat,
        audienceStatus: kind === 'exhibitor-announcement' ? audienceStatus : undefined,
        dryRun: false,
      });
      const data = (res.data?.data ?? res.data) as { campaignId: string; total: number; status: string };
      setCampaign({ id: data.campaignId, status: data.status, total: data.total });
      setInfo(`下書きを作成しました（対象 ${data.total} 名）。テスト送信して問題なければ送信を開始してください。`);
    } catch (e) {
      setError(extractError(e));
    } finally {
      setBusy(false);
    }
  };

  const sendTest = async () => {
    if (!campaign) return;
    if (!testEmail.includes('@')) return setError('テスト送信先メールアドレスを入力してください');
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/admin/email-campaigns/${campaign.id}/test-send`, { email: testEmail });
      setInfo(`${testEmail} にテスト送信しました。`);
    } catch (e) {
      setError(extractError(e));
    } finally {
      setBusy(false);
    }
  };

  const startSend = async () => {
    if (!campaign) return;
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/admin/email-campaigns/${campaign.id}/start`, {});
      setCampaign({ ...campaign, status: 'sending' });
      setInfo('送信を開始しました。毎分順次送信されます。');
    } catch (e) {
      setError(extractError(e));
    } finally {
      setBusy(false);
    }
  };

  const pauseSend = async () => {
    if (!campaign) return;
    setBusy(true);
    try {
      await api.post(`/api/admin/email-campaigns/${campaign.id}/pause`, {});
      setCampaign({ ...campaign, status: 'paused' });
    } catch (e) {
      setError(extractError(e));
    } finally {
      setBusy(false);
    }
  };

  const resetCompose = async () => {
    // 下書きのまま放置せず、未開始 (draft) なら中止して破棄する。
    if (campaign && campaign.status === 'draft') {
      try {
        await api.post(`/api/admin/email-campaigns/${campaign.id}/cancel`, {});
      } catch {
        /* 破棄失敗は無視 (draft は drain されないので無害) */
      }
    }
    setCampaign(null);
    setProgress(null);
    setInfo(null);
    setError(null);
  };

  const isNewsletter = kind === 'newsletter';

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="text-xl font-semibold text-kumo-default">メール一括送信</h1>
        <p className="text-sm text-kumo-muted">
          過去にアカウントを作成した方へのお知らせ（メルマガ）や、今年の出展者への業務連絡を一斉配信します。
        </p>
      </div>

      {error && <Banner variant="error">{error}</Banner>}
      {info && <Banner variant="default">{info}</Banner>}

      {!campaign && (
        <Surface className="flex flex-col gap-5 p-6">
          <Radio.Group
            legend="配信種別"
            appearance="card"
            value={kind}
            onValueChange={(v) => setKind(v as Kind)}
          >
            <Radio.Item
              label="メルマガ（全アカウント・配信停止あり）"
              description="過去にアカウントを作った全員（管理者を除く）へ送ります。各メールに配信停止リンクが付き、停止した人には届きません。今年の開催案内などの告知に。"
              value="newsletter"
            />
            <Radio.Item
              label="出展者向けお知らせ（今年の確定者・配信停止なし）"
              description="今年の出展者への業務連絡。搬入案内などの重要連絡に使います。配信停止リンクは付かず、必ず届きます。"
              value="exhibitor-announcement"
            />
          </Radio.Group>

          {!isNewsletter && (
            <>
              <Banner variant="alert">
                <strong>これは opt-out できない業務連絡です。</strong>{' '}
                出展に必要な連絡にのみ使用し、広告・宣伝の用途には使わないでください。
              </Banner>
              <Radio.Group
                legend="送信対象"
                appearance="card"
                value={audienceStatus}
                onValueChange={setAudienceStatus}
              >
                {ANNOUNCEMENT_STATUSES.map((s) => (
                  <Radio.Item key={s.value} label={s.label} description={s.description} value={s.value} />
                ))}
              </Radio.Group>
            </>
          )}

          <div className="flex items-center gap-2 text-sm text-kumo-default">
            <Badge>{isNewsletter ? 'メルマガ' : 'お知らせ'}</Badge>
            <span>
              配信対象:{' '}
              {previewCount === null ? '集計中…' : <strong>{previewCount.toLocaleString()} 名</strong>}
            </span>
            <Button size="sm" variant="ghost" onClick={fetchPreview}>
              再集計
            </Button>
          </div>

          <Radio.Group
            legend="本文形式"
            appearance="card"
            value={bodyFormat}
            onValueChange={(v) => setBodyFormat(v as BodyFormat)}
          >
            <Radio.Item
              label="ブランド枠あり"
              description="NT東京のヘッダ・フッタ・スタイル枠の中に、書いた HTML が入ります。手軽に体裁の整ったメールになります。"
              value="wrapped"
            />
            <Radio.Item
              label="枠なし（空テンプレート・生HTML）"
              description="書いた HTML をメール全文としてそのまま送ります。ヘッダ/フッタ/スタイルも自分で書く完全制御モード。"
              value="raw"
            />
          </Radio.Group>

          <Field label="件名">
            <Input
              value={subject}
              placeholder="例: NT東京2026 出展者募集を開始しました"
              onChange={(e) => setSubject(e.currentTarget.value)}
            />
          </Field>
          <Field
            label={
              bodyFormat === 'raw'
                ? 'HTML 本文（メール全文。<!DOCTYPE html> から自分で記述）'
                : 'HTML 本文（ブランド枠の中に入ります。HTMLタグ使用可）'
            }
          >
            <Textarea
              value={html}
              rows={14}
              placeholder={
                bodyFormat === 'raw'
                  ? '<!DOCTYPE html><html><body> ... </body></html>'
                  : '<p>本文を入力してください。</p>\n<p>HTMLタグ（&lt;a&gt; や &lt;strong&gt; など）が使えます。</p>'
              }
              className="font-mono text-xs"
              onChange={(e) => setHtml(e.currentTarget.value)}
            />
          </Field>
          <Field label="テキスト本文（任意。空欄なら HTML から自動生成。HTML非対応クライアント向け）">
            <Textarea
              value={text}
              rows={6}
              placeholder="空欄でOK（自動生成されます）"
              onChange={(e) => setText(e.currentTarget.value)}
            />
          </Field>
          {isNewsletter && (
            <p className="text-xs text-kumo-muted">
              ※ メール末尾に配信停止リンクが自動で追加されます。
            </p>
          )}

          <div>
            <Button
              variant="primary"
              icon={Megaphone}
              loading={busy}
              disabled={!subject.trim() || !html.trim() || previewCount === 0}
              onClick={createDraft}
            >
              下書きを作成（対象 {previewCount ?? '—'} 名）
            </Button>
          </div>
        </Surface>
      )}

      {campaign && (
        <Surface className="flex flex-col gap-5 p-6">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <Badge>{isNewsletter ? 'メルマガ' : 'お知らせ'}</Badge>
              <span className="text-sm text-kumo-default">
                対象 <strong>{campaign.total}</strong> 名 / 状態: <strong>{campaign.status}</strong>
              </span>
            </div>
            <Button size="sm" variant="ghost" onClick={resetCompose}>
              やり直す
            </Button>
          </div>

          <div className="rounded-md bg-kumo-base p-4">
            <div className="text-sm font-medium text-kumo-default">件名: {subject}</div>
            <div className="mt-1 text-xs text-kumo-muted">
              形式: {bodyFormat === 'raw' ? '枠なし（生HTML）' : 'ブランド枠あり'}
            </div>
            {/* admin が入力した HTML のプレビュー (枠は実送信時に付与) */}
            <div
              className="mt-3 max-h-80 overflow-auto rounded border border-kumo-muted/20 bg-white p-3 text-sm text-[#333]"
              dangerouslySetInnerHTML={{ __html: html }}
            />
          </div>

          {campaign.status === 'draft' && (
            <>
              <div className="flex flex-col gap-2">
                <Field label="テスト送信先（自分のメールアドレス推奨）">
                  <div className="flex gap-2">
                    <Input
                      value={testEmail}
                      placeholder="you@example.com"
                      onChange={(e) => setTestEmail(e.currentTarget.value)}
                    />
                    <Button variant="secondary" icon={TestTube} loading={busy} onClick={sendTest}>
                      テスト送信
                    </Button>
                  </div>
                </Field>
              </div>
              <Banner variant="alert">
                <strong>送信開始後の取り消しはできません。</strong>{' '}
                「送信を開始」を押すと {campaign.total} 名へ毎分順次メールが送られます。
              </Banner>
              <div>
                <Button variant="primary" icon={PaperPlaneTilt} loading={busy} onClick={startSend}>
                  送信を開始（{campaign.total} 名）
                </Button>
              </div>
            </>
          )}

          {(campaign.status === 'sending' || campaign.status === 'paused') && (
            <div className="flex items-center gap-3">
              <Button size="sm" variant="secondary" disabled={busy} onClick={campaign.status === 'sending' ? pauseSend : startSend}>
                {campaign.status === 'sending' ? '一時停止' : '再開'}
              </Button>
              <span className="text-xs text-kumo-muted">毎分自動送信中。画面を閉じても送信は継続します。</span>
            </div>
          )}

          {progress && (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
              {(['sent', 'pending', 'sending', 'skipped', 'failed'] as const).map((k) => (
                <div key={k} className="rounded-md border border-kumo-muted/20 p-3 text-center">
                  <div className="text-lg font-semibold text-kumo-default">{progress.counts[k] ?? 0}</div>
                  <div className="text-xs text-kumo-muted">{STATUS_LABEL[k]}</div>
                </div>
              ))}
            </div>
          )}

          {progress && progress.failed.length > 0 && (
            <div>
              <div className="mb-1 text-sm font-medium text-kumo-default">失敗（最大50件）</div>
              <Table>
                <Table.Header>
                  <Table.Row>
                    <Table.Head>メール</Table.Head>
                    <Table.Head>理由</Table.Head>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {progress.failed.map((f) => (
                    <Table.Row key={f.recipientEmail}>
                      <Table.Cell>{f.recipientEmail}</Table.Cell>
                      <Table.Cell>{f.error ?? '不明'}</Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table>
            </div>
          )}
        </Surface>
      )}

      <SuppressionPanel onError={setError} />
    </div>
  );
}

const STATUS_LABEL: Record<string, string> = {
  sent: '送信済み',
  pending: '待機中',
  sending: '送信中',
  skipped: 'スキップ',
  failed: '失敗',
};

// 配信停止 (抑制リスト) の管理。
function SuppressionPanel({ onError }: { onError: (msg: string) => void }) {
  const [rows, setRows] = useState<Suppression[]>([]);
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.get('/api/admin/email-campaigns/suppressions');
      setRows((res.data?.data ?? res.data) as Suppression[]);
    } catch (e) {
      onError(extractError(e));
    }
  }, [onError]);

  useEffect(() => {
    load();
  }, [load]);

  const add = async () => {
    if (!email.includes('@')) return onError('有効なメールアドレスを入力してください');
    setBusy(true);
    try {
      await api.post('/api/admin/email-campaigns/suppressions', { email });
      setEmail('');
      await load();
    } catch (e) {
      onError(extractError(e));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (target: string) => {
    setBusy(true);
    try {
      await api.delete(`/api/admin/email-campaigns/suppressions/${encodeURIComponent(target)}`);
      await load();
    } catch (e) {
      onError(extractError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Surface className="flex flex-col gap-4 p-6">
      <div>
        <h2 className="text-base font-semibold text-kumo-default">配信停止リスト（opt-out）</h2>
        <p className="text-sm text-kumo-muted">
          ここに登録されたアドレスにはメルマガが届きません（業務連絡・取引メールには影響しません）。
        </p>
      </div>
      <div className="flex gap-2">
        <Input value={email} placeholder="suppress@example.com" onChange={(e) => setEmail(e.currentTarget.value)} />
        <Button variant="secondary" icon={Plus} loading={busy} onClick={add}>
          追加
        </Button>
      </div>
      {rows.length === 0 ? (
        <p className="text-sm text-kumo-muted">登録はありません。</p>
      ) : (
        <Table>
          <Table.Header>
            <Table.Row>
              <Table.Head>メール</Table.Head>
              <Table.Head>理由</Table.Head>
              <Table.Head></Table.Head>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {rows.map((r) => (
              <Table.Row key={r.email}>
                <Table.Cell>{r.email}</Table.Cell>
                <Table.Cell>{r.reason}</Table.Cell>
                <Table.Cell>
                  <Button size="sm" variant="ghost" icon={TrashSimple} disabled={busy} onClick={() => remove(r.email)}>
                    解除
                  </Button>
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      )}
    </Surface>
  );
}

function extractError(e: unknown): string {
  if (e && typeof e === 'object' && 'response' in e) {
    const r = (e as { response?: { data?: { error?: string; message?: string } } }).response;
    return r?.data?.error ?? r?.data?.message ?? '処理に失敗しました';
  }
  return e instanceof Error ? e.message : '処理に失敗しました';
}
